// Treasury accounts module endpoint functions.
//
// Endpoints accept camelCase view-model inputs and return camelCase
// view-model outputs. Snake_case lives ONLY between the network and
// the parser.

import { adminApi } from '@core/api/httpClient'
import { TREASURY_ACCOUNTS_ENDPOINT_PATHS } from './treasuryAccountsEndpointPaths'
import type {
  CreateTreasuryBankAccountPayloadDto,
  IdempotentTreasuryBankAccountPayloadDto,
  TreasuryBankAccountDetailDto,
  TreasuryBankAccountDto,
  UpdateTreasuryBankAccountPayloadDto,
} from './treasuryAccountsContracts'
import type {
  CreateTreasuryAccountInput,
  GetTreasuryBankAccountsParams,
  IdempotentTreasuryAccountInput,
  TreasuryBankAccount,
  TreasuryBankAccountDetail,
  UpdateTreasuryAccountInput,
} from '../types/treasuryAccountsTypes'
import { parseTreasuryBankAccount, parseTreasuryBankAccountDetail } from './treasuryAccountsParser'

type WireListResponse = TreasuryBankAccountDto[] | { data: TreasuryBankAccountDto[] }
type WireDetailResponse = TreasuryBankAccountDetailDto | { data: TreasuryBankAccountDetailDto }

function unwrap<T>(payload: T | { data: T }): T {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

export async function getTreasuryBankAccounts(
  params: GetTreasuryBankAccountsParams,
): Promise<TreasuryBankAccount[]> {
  const queryParams: Record<string, string> = {}
  if (params.status) queryParams.status = params.status
  if (params.currencyCode) queryParams.currencyCode = params.currencyCode

  const { data } = await adminApi.get<WireListResponse>(TREASURY_ACCOUNTS_ENDPOINT_PATHS.LIST, {
    params: queryParams,
  })
  const rows = unwrap(data) ?? []
  return rows.map(parseTreasuryBankAccount)
}

export async function createTreasuryAccount(
  input: CreateTreasuryAccountInput,
): Promise<TreasuryBankAccountDetail> {
  const payload: CreateTreasuryBankAccountPayloadDto = {
    accountName: input.accountName,
    bankName: input.bankName,
    accountNumber: input.accountNumber,
    iban: input.iban,
    swiftBic: input.swiftBic,
    currencyCode: input.currencyCode,
    countryCode: input.countryCode,
    notes: input.notes,
    isDefault: input.isDefault,
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireDetailResponse>(
    TREASURY_ACCOUNTS_ENDPOINT_PATHS.CREATE,
    payload,
  )
  return parseTreasuryBankAccountDetail(unwrap(data))
}

export async function updateTreasuryAccount(
  input: UpdateTreasuryAccountInput,
): Promise<TreasuryBankAccountDetail> {
  const payload: UpdateTreasuryBankAccountPayloadDto = {}
  if (input.accountName !== undefined) payload.accountName = input.accountName
  if (input.bankName !== undefined) payload.bankName = input.bankName
  if (input.iban !== undefined) payload.iban = input.iban
  if (input.swiftBic !== undefined) payload.swiftBic = input.swiftBic
  if (input.notes !== undefined) payload.notes = input.notes

  const { data } = await adminApi.patch<WireDetailResponse>(
    TREASURY_ACCOUNTS_ENDPOINT_PATHS.UPDATE(input.id),
    payload,
  )
  return parseTreasuryBankAccountDetail(unwrap(data))
}

export async function deactivateTreasuryAccount(
  input: IdempotentTreasuryAccountInput,
): Promise<TreasuryBankAccountDetail> {
  const payload: IdempotentTreasuryBankAccountPayloadDto = {
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireDetailResponse>(
    TREASURY_ACCOUNTS_ENDPOINT_PATHS.DEACTIVATE(input.id),
    payload,
  )
  return parseTreasuryBankAccountDetail(unwrap(data))
}

export async function setDefaultTreasuryAccount(
  input: IdempotentTreasuryAccountInput,
): Promise<TreasuryBankAccountDetail> {
  const payload: IdempotentTreasuryBankAccountPayloadDto = {
    idempotencyKey: input.idempotencyKey,
  }
  const { data } = await adminApi.post<WireDetailResponse>(
    TREASURY_ACCOUNTS_ENDPOINT_PATHS.SET_DEFAULT(input.id),
    payload,
  )
  return parseTreasuryBankAccountDetail(unwrap(data))
}
